import React from 'react';
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Menu, X, User } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleBookNow = () => {
    navigate('/booking');
    setIsMobileMenuOpen(false);
  };

  const initials = user && user.name
    ? user.name.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()
    : "U";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/80 backdrop-blur-md shadow-sm py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
          <span
            className={`text-xl md:text-2xl font-bold tracking-wide whitespace-nowrap transition-colors ${
              isScrolled ? "text-foreground" : "text-white"
            }`}
          >
            CITIZEN <span className="text-secondary">TRAVELS</span>
          </span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {["Destinations", "Experiences", "Reviews", "About"].map((item) => (
            <a
              key={item}
              href={`/#${item.toLowerCase().replace(" ", "-")}`}
              className={`text-sm font-medium hover:text-secondary transition-colors ${
                isScrolled ? "text-foreground" : "text-white/90"
              }`}
            >
              {item}
            </a>
          ))}
          <Link
            to="/plan-trip"
            data-testid="nav-plan-trip"
            className={`text-sm font-medium hover:text-secondary transition-colors ${
              isScrolled ? "text-foreground" : "text-white/90"
            }`}
          >
            Plan Trip
          </Link>

          {user ? (
            <Link to="/account" data-testid="nav-account-link" className="flex items-center gap-2 group">
              <Avatar className="w-9 h-9 border border-white/30 group-hover:scale-105 transition-transform">
                <AvatarImage src={user.picture} alt={user.name} />
                <AvatarFallback className="bg-primary text-white text-xs">{initials}</AvatarFallback>
              </Avatar>
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="nav-signin-link"
              className={`text-sm font-medium hover:text-secondary transition-colors ${
                isScrolled ? "text-foreground" : "text-white/90"
              }`}
            >
              Sign In
            </Link>
          )}

          <Button
            variant={isScrolled ? "default" : "secondary"}
            className="rounded-full px-6 font-serif"
            onClick={handleBookNow}
            data-testid="nav-book-now"
          >
            Book Now
          </Button>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden text-foreground"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <X className={isScrolled ? "text-foreground" : "text-white"} />
          ) : (
            <Menu className={isScrolled ? "text-foreground" : "text-white"} />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-background border-b border-border p-4 flex flex-col gap-4 shadow-xl animate-in slide-in-from-top-5">
          {["Destinations", "Experiences", "Reviews", "About"].map((item) => (
            <a
              key={item}
              href={`/#${item.toLowerCase().replace(" ", "-")}`}
              className="text-lg font-medium text-foreground hover:text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {item}
            </a>
          ))}
          <Link
            to="/plan-trip"
            className="text-lg font-medium text-foreground hover:text-primary"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Plan Trip
          </Link>
          {user ? (
            <Link
              to="/account"
              className="text-lg font-medium text-foreground hover:text-primary flex items-center gap-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <User className="w-4 h-4" /> My Account
            </Link>
          ) : (
            <Link
              to="/login"
              className="text-lg font-medium text-foreground hover:text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Sign In
            </Link>
          )}
          <Button className="w-full rounded-full font-serif" onClick={handleBookNow}>Book Now</Button>
        </div>
      )}
    </nav>
  );
}
